import { useState, useMemo } from 'react'
import { useCollection } from '@cloudscape-design/collection-hooks'
import Alert from '@cloudscape-design/components/alert'
import Box from '@cloudscape-design/components/box'
import CollectionPreferences from '@cloudscape-design/components/collection-preferences'
import type { CollectionPreferencesProps } from '@cloudscape-design/components/collection-preferences'
import Header from '@cloudscape-design/components/header'
import Pagination from '@cloudscape-design/components/pagination'
import PropertyFilter from '@cloudscape-design/components/property-filter'
import type { PropertyFilterProps } from '@cloudscape-design/components/property-filter'
import SpaceBetween from '@cloudscape-design/components/space-between'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import Table from '@cloudscape-design/components/table'
import type { Provider } from '@/lib/types'
import { useProviders } from '@/hooks/useProviders'
import { formatDate } from '@/utils/format'

interface ProviderSelectionTableProps {
  clientId: string
  selectedProviders: Provider[]
  onSelectionChange: (items: Provider[]) => void
  /** If provided, overrides the internally fetched provider list */
  providers?: Provider[]
}

const STORAGE_KEY = 'providerSelectionTable.preferences'

const DEFAULT_PREFERENCES: CollectionPreferencesProps.Preferences = {
  pageSize: 20,
  contentDisplay: [
    { id: 'provider_name', visible: true },
    { id: 'specialty', visible: true },
    { id: 'location', visible: true },
    { id: 'email', visible: true },
    { id: 'reachedOut', visible: true },
    { id: 'last_outreach', visible: true },
  ],
}

function loadPreferences(): CollectionPreferencesProps.Preferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as CollectionPreferencesProps.Preferences) : DEFAULT_PREFERENCES
  } catch {
    return DEFAULT_PREFERENCES
  }
}

const FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  {
    key: 'provider_name',
    propertyLabel: 'Name',
    groupValuesLabel: 'Name values',
    operators: [':', '!:', '=', '!='],
  },
  {
    key: 'specialty',
    propertyLabel: 'Specialty',
    groupValuesLabel: 'Specialty values',
    operators: [':', '!:', '=', '!='],
  },
  {
    key: 'city',
    propertyLabel: 'City',
    groupValuesLabel: 'City values',
    operators: [':', '!:', '=', '!='],
  },
  {
    key: 'state',
    propertyLabel: 'State',
    groupValuesLabel: 'State values',
    operators: ['=', '!='],
  },
  {
    key: 'status',
    propertyLabel: 'Status',
    groupValuesLabel: 'Status values',
    operators: ['=', '!='],
  },
  {
    key: 'npi',
    propertyLabel: 'NPI',
    groupValuesLabel: 'NPI values',
    operators: ['='],
  },
]

const PROPERTY_FILTER_STRINGS = {
  filteringAriaLabel: 'Filter providers',
  filteringPlaceholder: 'Filter by name, specialty, location…',
  groupPropertiesText: 'Properties',
  groupValuesText: 'Values',
  operatorsText: 'Operators',
  operationAndText: 'and',
  operationOrText: 'or',
  operatorLessText: 'Less than',
  operatorLessOrEqualText: 'Less than or equal',
  operatorGreaterText: 'Greater than',
  operatorGreaterOrEqualText: 'Greater than or equal',
  operatorContainsText: 'Contains',
  operatorDoesNotContainText: 'Does not contain',
  operatorEqualsText: 'Equals',
  operatorDoesNotEqualText: 'Does not equal',
  editTokenHeader: 'Edit filter',
  propertyText: 'Property',
  operatorText: 'Operator',
  valueText: 'Value',
  cancelActionText: 'Cancel',
  applyActionText: 'Apply',
  allPropertiesLabel: 'All properties',
  tokenLimitShowMore: 'Show more',
  tokenLimitShowFewer: 'Show fewer',
  clearFiltersText: 'Clear filters',
  removeTokenButtonAriaLabel: (token: { propertyKey?: string; operator: string; value: string }) =>
    `Remove filter: ${token.propertyKey ?? ''} ${token.operator} ${token.value}`,
  enteredTextLabel: (text: string) => `Use: "${text}"`,
}

export default function ProviderSelectionTable({
  clientId,
  selectedProviders,
  onSelectionChange,
  providers: providersProp,
}: ProviderSelectionTableProps) {
  const { providers: fetchedProviders, loading } = useProviders(clientId)
  const providers = providersProp ?? fetchedProviders
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>(loadPreferences)

  // Include manually added providers that haven't yet been flushed to DB + refetched
  const allProviders = useMemo(() => {
    const dbIds = new Set(providers.map(p => p._id))
    const localOnly = selectedProviders.filter(p => !dbIds.has(p._id))
    return localOnly.length > 0 ? [...providers, ...localOnly] : providers
  }, [providers, selectedProviders])

  // Duplicate-email guard: compute which selected providers share an email
  const { duplicateProviderIds, claimedEmails } = useMemo(() => {
    const emailToFirstId = new Map<string, string>()
    const dupeIds = new Set<string>()
    for (const p of selectedProviders) {
      if (p.email) {
        const lower = p.email.toLowerCase()
        if (emailToFirstId.has(lower)) {
          dupeIds.add(p._id)
        } else {
          emailToFirstId.set(lower, p._id)
        }
      }
    }
    return { duplicateProviderIds: dupeIds, claimedEmails: emailToFirstId }
  }, [selectedProviders])

  const hasDuplicates = duplicateProviderIds.size > 0

  const { items, collectionProps, paginationProps, propertyFilterProps } = useCollection(
    allProviders,
    {
      propertyFiltering: {
        filteringProperties: FILTERING_PROPERTIES,
        empty: <Box textAlign="center" color="text-body-secondary" padding="l">No providers found</Box>,
        noMatch: <Box textAlign="center" color="text-body-secondary" padding="l">No providers match the current filters</Box>,
      },
      sorting: {
        defaultState: {
          isDescending: false,
          sortingColumn: { sortingField: 'provider_name' },
        },
      },
      pagination: { pageSize: preferences.pageSize ?? 20 },
    },
  )

  function handlePreferencesConfirm({ detail }: { detail: CollectionPreferencesProps.Preferences }) {
    setPreferences(detail)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(detail))
  }

  return (
    <SpaceBetween size="s">
      {hasDuplicates && (
        <Alert type="warning">
          Two selected providers share the same email address. Please deselect one.
        </Alert>
      )}
      <Table
        {...collectionProps}
        selectionType="multi"
        trackBy="_id"
        selectedItems={selectedProviders}
        onSelectionChange={(e) => onSelectionChange(e.detail.selectedItems)}
        isItemDisabled={(item) => {
          if (!item.email) return false
          const lower = item.email.toLowerCase()
          // Disable if this email is claimed by a *different* selected provider
          const claimedBy = claimedEmails.get(lower)
          return claimedBy !== undefined && claimedBy !== item._id
        }}
        loading={loading}
        loadingText="Loading providers…"
        resizableColumns
        columnDisplay={preferences.contentDisplay}
        columnDefinitions={[
          {
            id: 'provider_name',
            header: 'Name',
            cell: (item) => item.provider_name,
            sortingField: 'provider_name',
          },
          {
            id: 'specialty',
            header: 'Specialty',
            cell: (item) => item.specialty || <Box color="text-body-secondary">—</Box>,
            sortingField: 'specialty',
          },
          {
            id: 'location',
            header: 'Location',
            cell: (item) => {
              const parts = [item.city, item.state].filter(Boolean)
              return parts.length > 0 ? parts.join(', ') : <Box color="text-body-secondary">—</Box>
            },
            sortingField: 'city',
          },
          {
            id: 'email',
            header: 'Email',
            cell: (item) =>
              item.email ? (
                <StatusIndicator type="success">Available</StatusIndicator>
              ) : (
                <StatusIndicator type="error">Missing</StatusIndicator>
              ),
            sortingComparator: (a, b) => (a.email ? 1 : 0) - (b.email ? 1 : 0),
          },
          {
            id: 'reachedOut',
            header: 'Reached Out',
            cell: (item) =>
              item.outreach_email != null ? (
                <StatusIndicator type="success">Yes</StatusIndicator>
              ) : (
                <StatusIndicator type="stopped">No</StatusIndicator>
              ),
            sortingComparator: (a, b) =>
              (!!a.outreach_email ? 1 : 0) - (!!b.outreach_email ? 1 : 0),
          },
          {
            id: 'last_outreach',
            header: 'Last Outreach',
            cell: (item) => {
              const sentAt = item.outreach_email?.sent_at
              return sentAt ? formatDate(sentAt) : <Box color="text-body-secondary">Never</Box>
            },
            sortingComparator: (a, b) => {
              const aTime = a.outreach_email?.sent_at ? new Date(a.outreach_email.sent_at).getTime() : 0
              const bTime = b.outreach_email?.sent_at ? new Date(b.outreach_email.sent_at).getTime() : 0
              return aTime - bTime
            },
          },
        ]}
        items={items}
        header={
          <Header
            counter={
              selectedProviders.length > 0
                ? `(${selectedProviders.length} selected / ${providers.length})`
                : `(${providers.length})`
            }
          >
            Providers
          </Header>
        }
        filter={
          <PropertyFilter
            {...propertyFilterProps}
            filteringProperties={FILTERING_PROPERTIES}
            i18nStrings={PROPERTY_FILTER_STRINGS}
          />
        }
        pagination={<Pagination {...paginationProps} />}
        preferences={
          <CollectionPreferences
            title="Table preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={preferences}
            onConfirm={handlePreferencesConfirm}
            pageSizePreference={{
              title: 'Page size',
              options: [
                { value: 10, label: '10 providers' },
                { value: 20, label: '20 providers' },
                { value: 50, label: '50 providers' },
                { value: 100, label: '100 providers' },
              ],
            }}
            contentDisplayPreference={{
              title: 'Column preferences',
              description: 'Choose which columns to display and in what order.',
              options: [
                { id: 'provider_name', label: 'Name', alwaysVisible: true },
                { id: 'specialty', label: 'Specialty' },
                { id: 'location', label: 'Location' },
                { id: 'email', label: 'Email' },
                { id: 'reachedOut', label: 'Reached Out' },
                { id: 'last_outreach', label: 'Last Outreach' },
              ],
            }}
          />
        }
        empty={
          <Box textAlign="center" color="text-body-secondary" padding="xxl">
            <Box variant="strong">No providers found</Box>
            <Box variant="p">Add providers in the Directory tab.</Box>
          </Box>
        }
      />
    </SpaceBetween>
  )
}
